import { loadStripe, StripeElementsOptionsMode } from '@stripe/stripe-js'
import { useMemo } from 'react'

import { CheckoutFormProps } from '@/components/checkout/checkout-form'
import { CheckoutFormWrapper } from '@/components/checkout/checkout-form-wrapper'
import { StripeExpressCheckoutProps } from '@/components/checkout/stripe-express-checkout'
import { StripeExpressCheckoutWrapper } from '@/components/checkout/stripe-express-checkout-wrapper'
import { env } from '@/env'
import { useFlag } from '@/lib/hooks/use-flag'
import { useStripeElementsDefaultOptions } from '@/lib/hooks/use-stripe-elements-default-options'
import { FeatureFlag } from '@/lib/posthog/features'

import { IppsCheckout } from './ipps-checkout'

type Props = {
  options: StripeElementsOptionsMode
} & CheckoutFormProps &
  StripeExpressCheckoutProps

export const CheckoutWrapper = ({
  storeConfigData,
  onPaymentInitiated,
  onPaymentUpdated,
  onPaymentConfirmed,
  getPaymentParams,
  siteSlug,
  options,
  currentUser,
  defaultValues,
  collectAddress,
  requiresShipping,
  children,
}: Props) => {
  const ippsIntegrationEnabled = useFlag(FeatureFlag.IPPS_INTEGRATION)

  const stripeAccountId = storeConfigData?.stripeLinkedAccount?.stripeAccountId

  const stripePromise = useMemo(() => {
    if (!stripeAccountId) {
      return null
    }

    return loadStripe(env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY, {
      stripeAccount: stripeAccountId,
    })
  }, [stripeAccountId])

  const elementsOptions = useStripeElementsDefaultOptions(options)

  if (ippsIntegrationEnabled) {
    return (
      <IppsCheckout
        siteSlug={siteSlug}
        defaultValues={defaultValues}
        currentUser={currentUser}
        options={options}
        getPaymentParams={getPaymentParams}
        onPaymentInitiated={onPaymentInitiated}
        onPaymentUpdated={onPaymentUpdated}
        onPaymentConfirmed={onPaymentConfirmed}
      />
    )
  }

  return (
    <>
      {!requiresShipping && (
        <StripeExpressCheckoutWrapper
          options={elementsOptions}
          siteSlug={siteSlug}
          stripePromise={stripePromise}
          getPaymentParams={getPaymentParams}
          onPaymentInitiated={onPaymentInitiated}
          onPaymentUpdated={onPaymentUpdated}
          onPaymentConfirmed={onPaymentConfirmed}
        />
      )}

      <CheckoutFormWrapper
        siteSlug={siteSlug}
        currentUser={currentUser}
        stripePromise={stripePromise}
        defaultValues={defaultValues}
        options={elementsOptions}
        getPaymentParams={getPaymentParams}
        onPaymentInitiated={onPaymentInitiated}
        onPaymentUpdated={onPaymentUpdated}
        onPaymentConfirmed={onPaymentConfirmed}
        collectAddress={collectAddress}
        requiresShipping={requiresShipping}
      >
        {children}
      </CheckoutFormWrapper>
    </>
  )
}
